/**
 * Audio Feedback Service for Illiterate Users
 * Provides sound and voice announcements for all app events
 */

export type SoundType = 
  | 'tap'           // Button press confirmation
  | 'success'       // Action completed successfully  
  | 'error'         // Something went wrong
  | 'reward'        // Points earned (celebratory)
  | 'levelUp'       // Reached new level (big celebration)
  | 'loading'       // Processing (subtle loop)
  | 'camera'        // Photo captured
  | 'notification'  // New notification arrived
  | 'warning';      // Attention needed

interface SoundConfig {
  frequency: number;
  duration: number;
  type: OscillatorType;
  ramp?: number; // Frequency multiplier for ramping
}

export interface AudioFeedbackOptions {
  vibrate?: boolean;        // Also trigger haptic feedback
  voiceMessage?: string;    // Optional voice announcement
  volume?: number;          // 0-1, default 0.7
}

// Localized voice messages for common actions
const voiceMessages: Record<string, Record<string, string>> = {
  photoSaved: {
    en: 'Photo saved!',
    bn: 'ছবি সংরক্ষিত!',
    my: 'ဓာတ်ပုံ သိမ်းပြီး!',
    sw: 'Picha imehifadhiwa!',
    km: 'រូបភាពបានរក្សាទុក!',
    uk: 'Фото збережено!',
    la: 'ຮູບບັນທຶກແລ້ວ!',
    vi: 'Đã lưu ảnh!',
  },
  activityLogged: {
    en: 'Activity logged!',
    bn: 'কার্যকলাপ লিপিবদ্ধ!',
    my: 'လုပ်ဆောင်ချက် မှတ်တမ်းတင်ပြီး!',
    sw: 'Shughuli imerekodiwa!',
    km: 'សកម្មភាពបានកត់ត្រា!',
    uk: 'Активність записано!',
    la: 'ບັນທຶກກິດຈະກຳແລ້ວ!',
    vi: 'Đã ghi hoạt động!',
  },
  voiceRecorded: {
    en: 'Voice recorded!',
    bn: 'ভয়েস রেকর্ড হয়েছে!',
    my: 'အသံ မှတ်တမ်းတင်ပြီး!',
    sw: 'Sauti imerekodiwa!',
    km: 'បានថតសំឡេង!',
    uk: 'Голос записано!',
    la: 'ບັນທຶກສຽງແລ້ວ!',
    vi: 'Đã ghi âm!',
  },
  pointsEarned: {
    en: 'Plus {points} points!',
    bn: 'প্লাস {points} পয়েন্ট!',
    my: 'အမှတ် {points} ရရှိ!',
    sw: 'Alama {points} zaidi!',
    km: 'បូក {points} ពិន្ទុ!',
    uk: 'Плюс {points} балів!',
    la: 'ບວກ {points} ຄະແນນ!',
    vi: 'Cộng {points} điểm!',
  },
  levelUp: {
    en: 'Congratulations! You reached a new level!',
    bn: 'অভিনন্দন! আপনি নতুন স্তরে পৌঁছেছেন!',
    my: 'ဂုဏ်ယူပါတယ်! အဆင့်သစ် ရောက်ပြီ!',
    sw: 'Hongera! Umefikia kiwango kipya!',
    km: 'សូមអបអរសាទរ! អ្នកបានឈានដល់កម្រិតថ្មី!',
    uk: 'Вітаємо! Ви досягли нового рівня!',
    la: 'ຊົມເຊີຍ! ເຈົ້າຮອດລະດັບໃໝ່ແລ້ວ!',
    vi: 'Chúc mừng! Bạn đã đạt cấp độ mới!',
  },
  error: {
    en: 'Something went wrong. Please try again.',
    bn: 'কিছু ভুল হয়েছে। আবার চেষ্টা করুন।',
    my: 'တစ်ခုခု မှားသွားပြီ။ ထပ်ကြိုးစားပါ။',
    sw: 'Kuna hitilafu. Tafadhali jaribu tena.',
    km: 'មានបញ្ហា។ សូមព្យាយាមម្តងទៀត។',
    uk: 'Щось пішло не так. Спробуйте ще раз.',
    la: 'ມີບັນຫາ. ກະລຸນາລອງໃໝ່.',
    vi: 'Đã xảy ra lỗi. Vui lòng thử lại.',
  },
  warning: {
    en: 'Attention needed!',
    bn: 'মনোযোগ দরকার!',
    my: 'အာရုံစိုက်ရန် လိုအပ်!',
    sw: 'Uangalifu unahitajika!',
    km: 'ត្រូវការការយកចិត្តទុកដាក់!',
    uk: 'Потребує уваги!',
    la: 'ຕ້ອງການຄວາມສົນໃຈ!',
    vi: 'Cần chú ý!',
  },
  welcomeBack: {
    en: 'Welcome back! You have {tasks} tasks today.',
    bn: 'স্বাগতম! আজ আপনার {tasks}টি কাজ আছে।',
    my: 'ပြန်လာတာ ကြိုဆိုပါတယ်! ဒီနေ့ အလုပ် {tasks} ခု ရှိတယ်။',
    sw: 'Karibu tena! Una kazi {tasks} leo.',
    km: 'សូមស្វាគមន៍មកវិញ! អ្នកមានកិច្ចការ {tasks} ថ្ងៃនេះ។',
    uk: 'З поверненням! У вас {tasks} завдань сьогодні.',
    la: 'ຍິນດີຕ້ອນຮັບກັບມາ! ມື້ນີ້ເຈົ້າມີ {tasks} ໜ້າວຽກ.',
    vi: 'Chào mừng trở lại! Hôm nay bạn có {tasks} nhiệm vụ.',
    tl: 'Maligayang pagbabalik! May {tasks} gawain ka ngayon.',
    ur: 'واپسی پر خوش آمدید! آج آپ کے {tasks} کام ہیں۔',
  },
  scoreAnnouncement: {
    en: 'Your score is {score} points.',
    bn: 'আপনার স্কোর {score} পয়েন্ট।',
    my: 'သင့်အမှတ် {score} ဖြစ်ပါတယ်။',
    sw: 'Alama yako ni {score}.',
    km: 'ពិន្ទុរបស់អ្នកគឺ {score}។',
    uk: 'Ваш бал — {score} очок.',
    la: 'ຄະແນນຂອງເຈົ້າແມ່ນ {score} ຄະແນນ.',
    vi: 'Điểm của bạn là {score}.',
    tl: 'Ang iyong puntos ay {score}.',
    ur: 'آپ کا سکور {score} پوائنٹس ہے۔',
  },
  taskComplete: {
    en: 'Well done! Plus {points} points!',
    bn: 'চমৎকার! প্লাস {points} পয়েন্ট!',
    my: 'ကောင်းပြီ! အမှတ် {points} ရရှိ!',
    sw: 'Vizuri! Alama {points} zaidi!',
    km: 'អស្ចារ្យ! បូក {points} ពិន្ទុ!',
    uk: 'Чудово! Плюс {points} балів!',
    la: 'ດີຫຼາຍ! ບວກ {points} ຄະແນນ!',
    vi: 'Tuyệt vời! Cộng {points} điểm!',
    tl: 'Magaling! Dagdag {points} puntos!',
    ur: 'شاباش! پلس {points} پوائنٹس!',
  },
  allTasksComplete: {
    en: 'All tasks done! Great job today!',
    bn: 'সব কাজ শেষ! আজ দারুণ কাজ!',
    my: 'အလုပ်အားလုံး ပြီးပြီ! ဒီနေ့ ကောင်းတယ်!',
    sw: 'Kazi zote zimekamilika! Hongera!',
    km: 'កិច្ចការទាំងអស់រួចរាល់! អស្ចារ្យណាស់!',
    uk: 'Усі завдання виконано! Молодці!',
    la: 'ໜ້າວຽກທັງໝົດສຳເລັດແລ້ວ! ເກັ່ງຫຼາຍ!',
    vi: 'Hoàn thành tất cả! Làm tốt lắm!',
    tl: 'Tapos na lahat! Mahusay ka ngayon!',
    ur: 'سب کام مکمل! آج بہت اچھا کام!',
  },
  photoAnalysis: {
    en: 'Your crop looks {health}!',
    bn: 'আপনার ফসল দেখতে {health}!',
    my: 'သင့်သီးနှံ {health} ဖြစ်နေတယ်!',
    sw: 'Mazao yako yanaonekana {health}!',
    km: 'ដំណាំរបស់អ្នកមើលទៅ {health}!',
    uk: 'Ваш врожай виглядає {health}!',
    la: 'ພືດຂອງເຈົ້າເບິ່ງ {health}!',
    vi: 'Cây trồng của bạn trông {health}!',
    tl: 'Ang iyong pananim ay mukhang {health}!',
    ur: 'آپ کی فصل {health} لگ رہی ہے!',
  },
  streakCongrats: {
    en: 'Amazing! {days} day streak!',
    bn: 'দারুণ! {days} দিনের ধারাবাহিকতা!',
    my: 'အံ့မခန်း! {days} ရက်ဆက်တိုက်!',
    sw: 'Ajabu! Siku {days} mfululizo!',
    km: 'អស្ចារ្យ! {days} ថ្ងៃជាប់គ្នា!',
    uk: 'Вражаюче! {days} днів поспіль!',
    la: 'ອັດສະຈັນ! {days} ມື້ຕິດຕໍ່ກັນ!',
    vi: 'Tuyệt vời! Chuỗi {days} ngày!',
    tl: 'Kahanga-hanga! {days} araw na sunod-sunod!',
    ur: 'شاندار! {days} دن مسلسل!',
  },
  offlineNotice: {
    en: 'No internet. Your work is saved.',
    bn: 'ইন্টারনেট নেই। আপনার কাজ সংরক্ষিত।',
    my: 'အင်တာနက်မရှိ။ အလုပ် သိမ်းထားပြီ။',
    sw: 'Hakuna mtandao. Kazi yako imehifadhiwa.',
    km: 'គ្មានអ៊ីនធឺណិត។ ការងាររបស់អ្នកបានរក្សាទុក។',
    uk: 'Немає інтернету. Вашу роботу збережено.',
    la: 'ບໍ່ມີອິນເຕີເນັດ. ວຽກຂອງເຈົ້າບັນທຶກແລ້ວ.',
    vi: 'Không có mạng. Công việc đã được lưu.',
    tl: 'Walang internet. Naka-save ang iyong gawa.',
    ur: 'انٹرنیٹ نہیں ہے۔ آپ کا کام محفوظ ہے۔',
  },
};

class AudioFeedbackService {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;
  private voiceEnabled: boolean = true;
  private speechSynthesis: SpeechSynthesis | null = null;
  private soundConfigs: Record<SoundType, SoundConfig>;

  constructor() {
    this.soundConfigs = {
      tap: { frequency: 800, duration: 0.05, type: 'sine' },
      success: { frequency: 880, duration: 0.15, type: 'sine', ramp: 1.3 },
      error: { frequency: 200, duration: 0.3, type: 'square' },
      reward: { frequency: 1200, duration: 0.2, type: 'sine', ramp: 1.5 },
      levelUp: { frequency: 800, duration: 0.5, type: 'sine', ramp: 2 },
      loading: { frequency: 400, duration: 0.1, type: 'sine' },
      camera: { frequency: 1000, duration: 0.08, type: 'sine' },
      notification: { frequency: 600, duration: 0.15, type: 'triangle' },
      warning: { frequency: 300, duration: 0.2, type: 'sawtooth' },
    };

    this.init();
  }

  private init() {
    if (typeof window !== 'undefined') {
      this.speechSynthesis = window.speechSynthesis;
      
      // Load saved preferences
      const savedAudio = localStorage.getItem('agxl_audio_enabled');
      const savedVoice = localStorage.getItem('agxl_voice_enabled');
      
      if (savedAudio !== null) {
        this.enabled = savedAudio === 'true';
      }
      if (savedVoice !== null) {
        this.voiceEnabled = savedVoice === 'true';
      }
    }
  }

  private async getAudioContext(): Promise<AudioContext> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    
    // Resume if suspended (required for autoplay policies)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    return this.audioContext;
  }

  async play(sound: SoundType, options: AudioFeedbackOptions = {}) {
    if (!this.enabled) return;

    const { vibrate = true, voiceMessage, volume = 0.7 } = options;
    const config = this.soundConfigs[sound];

    // Play synthesized sound
    try {
      const ctx = await this.getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = config.type;
      oscillator.frequency.setValueAtTime(config.frequency, ctx.currentTime);

      // Add frequency ramping for success/reward sounds
      if (config.ramp) {
        oscillator.frequency.linearRampToValueAtTime(
          config.frequency * config.ramp,
          ctx.currentTime + config.duration
        );
      }

      // For level up, play a chord-like effect
      if (sound === 'levelUp') {
        this.playChord(ctx, volume);
      }

      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + config.duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start();
      oscillator.stop(ctx.currentTime + config.duration);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }

    // Haptic feedback
    if (vibrate && 'vibrate' in navigator) {
      const patterns: Record<SoundType, number | number[]> = {
        tap: 10,
        success: [50, 30, 50],
        error: [100, 50, 100, 50, 100],
        reward: [50, 30, 50, 30, 100],
        levelUp: [100, 50, 100, 50, 200],
        loading: 20,
        camera: 30,
        notification: [50, 50, 50],
        warning: [80, 40, 80],
      };
      navigator.vibrate(patterns[sound]);
    }

    // Voice announcement
    if (voiceMessage && this.voiceEnabled && this.speechSynthesis) {
      this.speak(voiceMessage);
    }
  }

  private async playChord(ctx: AudioContext, volume: number) {
    // Play a triumphant chord for level up
    const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 (C major chord)
    
    frequencies.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);
        gain.gain.setValueAtTime(volume * 0.5, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start();
        osc.stop(ctx.currentTime + 0.5);
      }, i * 100);
    });
  }

  private speak(message: string) {
    if (!this.speechSynthesis || !this.voiceEnabled) return;
    
    // Cancel any ongoing speech
    this.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(message);
    utterance.lang = this.getLanguageCode();
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.volume = 1.0;
    
    this.speechSynthesis.speak(utterance);
  }

  private getCurrentLanguage(): string {
    return localStorage.getItem('i18nextLng')?.split('-')[0] || 'en';
  }

  private getLanguageCode(): string {
    const langMap: Record<string, string> = {
      en: 'en-US',
      bn: 'bn-BD',
      my: 'my-MM',
      sw: 'sw-TZ',
      km: 'km-KH',
      uk: 'uk-UA',
      la: 'lo-LA',
      vi: 'vi-VN',
    };
    return langMap[this.getCurrentLanguage()] || 'en-US';
  }

  getVoiceMessage(key: keyof typeof voiceMessages, replacements?: Record<string, string | number>): string {
    const lang = this.getCurrentLanguage();
    let message = voiceMessages[key]?.[lang] || voiceMessages[key]?.['en'] || '';
    
    if (replacements) {
      Object.entries(replacements).forEach(([key, value]) => {
        message = message.replace(`{${key}}`, String(value));
      });
    }
    
    return message;
  }

  // Convenience methods
  success(message?: string) {
    this.play('success', { voiceMessage: message });
  }

  error(message?: string) {
    const voiceMsg = message || this.getVoiceMessage('error');
    this.play('error', { voiceMessage: voiceMsg });
  }

  reward(points: number) {
    const voiceMsg = this.getVoiceMessage('pointsEarned', { points });
    this.play('reward', { voiceMessage: voiceMsg });
  }

  levelUp(stageName?: string) {
    const voiceMsg = this.getVoiceMessage('levelUp');
    this.play('levelUp', { voiceMessage: voiceMsg });
  }

  photoSaved() {
    const voiceMsg = this.getVoiceMessage('photoSaved');
    this.play('camera', { voiceMessage: voiceMsg });
  }

  activityLogged() {
    const voiceMsg = this.getVoiceMessage('activityLogged');
    this.play('success', { voiceMessage: voiceMsg });
  }

  voiceRecorded() {
    const voiceMsg = this.getVoiceMessage('voiceRecorded');
    this.play('success', { voiceMessage: voiceMsg });
  }

  warning(message?: string) {
    const voiceMsg = message || this.getVoiceMessage('warning');
    this.play('warning', { voiceMessage: voiceMsg });
  }

  tap() {
    this.play('tap', { vibrate: true });
  }

  welcomeBack(tasks: number) {
    const voiceMsg = this.getVoiceMessage('welcomeBack', { tasks });
    this.play('notification', { voiceMessage: voiceMsg });
  }

  scoreAnnouncement(score: number) {
    const voiceMsg = this.getVoiceMessage('scoreAnnouncement', { score });
    this.play('success', { voiceMessage: voiceMsg });
  }

  taskComplete(points: number) {
    const voiceMsg = this.getVoiceMessage('taskComplete', { points });
    this.play('reward', { voiceMessage: voiceMsg });
  }

  allTasksComplete() {
    const voiceMsg = this.getVoiceMessage('allTasksComplete');
    this.play('levelUp', { voiceMessage: voiceMsg });
  }

  offlineNotice() {
    const voiceMsg = this.getVoiceMessage('offlineNotice');
    this.play('warning', { voiceMessage: voiceMsg });
  }

  // Settings
  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    localStorage.setItem('agxl_audio_enabled', String(enabled));
  }

  setVoiceEnabled(enabled: boolean) {
    this.voiceEnabled = enabled;
    localStorage.setItem('agxl_voice_enabled', String(enabled));
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isVoiceEnabled(): boolean {
    return this.voiceEnabled;
  }
}

export const audioFeedback = new AudioFeedbackService();
